import React, { useState } from "react";
import { Plus, Trash2, Waypoints } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/shared/components/ui/dialog";
import { FieldLabel, TextInput, SelectInput, PrimaryButton, GhostButton } from "@/shared/components/settings-atoms";
import { CHECK_TYPES, CHECK_FAMILIES, makeCase, makeCheck } from "@/shared/lib/evals-model";
import ModelPicker from "@/shared/components/models/ModelPicker";
import { TagChip } from "./atoms";

import type { ReactNode } from "react";

import type { EvalCase, EvalCheck } from "@/shared/types";
import type { ModelSelection } from "@/shared/types/models";

/* Create or edit one case, plus its checks. Also the checks editor for the
   suite-wide list — same component, `caseMode={false}` drops the input/expected
   fields, so there is one place where a check gets configured. */

const textareaCls =
  "w-full px-3 py-2.5 rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[13.5px] leading-[1.6] text-[var(--text-primary)] outline-none focus:ring-[var(--hairline-focus)] placeholder:text-[var(--text-quaternary)] transition-shadow resize-y";

/* Family is prefixed into the label rather than rendered as a disabled group
   header — SelectInput takes a flat {id,label} list, and a fake separator row
   the user can actually select is worse than no separator. */
const TYPE_OPTIONS = CHECK_FAMILIES.flatMap(([family, label]) =>
  Object.entries(CHECK_TYPES)
    .filter(([, m]) => m.family === family)
    .map(([id, m]) => ({ id, label: `${label} · ${m.label}` }))
);

function CheckEditor({ check, onChange, onRemove }: {
  check: EvalCheck;
  onChange: (next: EvalCheck) => void;
  onRemove: () => void;
}) {
  const meta = CHECK_TYPES[check.type];
  return (
    <div className="rounded-[10px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] p-3">
      <div className="flex items-center gap-2">
        <SelectInput
          compact
          className="w-[168px]"
          value={check.type}
          onChange={(type: string) => onChange({ ...check, type: type as EvalCheck["type"] })}
          options={TYPE_OPTIONS}
        />
        <span className="flex-1 min-w-0 text-[11.5px] text-[var(--text-quaternary)] truncate">{meta?.hint}</span>
        <button
          onClick={onRemove}
          title="Remove check"
          className="press shrink-0 w-7 h-7 rounded-[7px] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--danger)] hover:bg-[var(--accent-hover)]"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
      </div>
      {!!meta?.fields.length && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2.5">
          {meta.fields.map((f) => (
            <div key={f.key} className={f.multiline ? "sm:col-span-2" : ""}>
              <FieldLabel>{f.label}</FieldLabel>
              {f.type === "model" ? (
                <JudgeModel check={check} onChange={onChange} />
              ) : f.options ? (
                <SelectInput
                  value={check[f.key as keyof EvalCheck] || f.options[0]}
                  onChange={(v: string) => onChange({ ...check, [f.key]: v })}
                  options={f.options.map((o) => ({ id: o, label: o }))}
                />
              ) : f.multiline ? (
                <textarea
                  rows={3}
                  value={check[f.key as keyof EvalCheck] || ""}
                  placeholder={f.placeholder}
                  onChange={(e) => onChange({ ...check, [f.key]: e.target.value })}
                  className={textareaCls}
                />
              ) : (
                <TextInput
                  type={f.type || "text"}
                  value={check[f.key as keyof EvalCheck] || ""}
                  placeholder={f.placeholder}
                  onChange={(v: string) => onChange({ ...check, [f.key]: v })}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* The judge grades on the agent's own model unless one is picked here — the
   picker's "none" answer is exactly that default. */
function JudgeModel({ check, onChange }: { check: EvalCheck; onChange: (next: EvalCheck) => void }) {
  const value: ModelSelection | null = !check.model
    ? null
    : check.connectionId
      ? { modelId: check.model, source: "connection", connectionId: check.connectionId }
      : { modelId: check.model, source: "managed" };
  return (
    <ModelPicker
      value={value}
      onChange={(selection) =>
        onChange({
          ...check,
          model: selection.modelId,
          connectionId: selection.source === "connection" ? selection.connectionId : undefined,
        })}
      none={{
        label: "The agent's own model",
        detail: "Graded on the model and key the agent runs on",
        onSelect: () => onChange({ ...check, model: undefined, connectionId: undefined }),
      }}
    />
  );
}

export function CheckList({ checks, onChange, emptyHint }: {
  checks: EvalCheck[];
  onChange: (next: EvalCheck[]) => void;
  emptyHint: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      {checks.length === 0 && (
        <div className="py-8 text-center rounded-[10px] border border-dashed border-[var(--hairline-strong)]">
          <div className="text-[12.5px] text-[var(--text-tertiary)]">{emptyHint}</div>
        </div>
      )}
      {checks.map((c, i) => (
        <CheckEditor
          key={c.id}
          check={c}
          onChange={(next) => onChange(checks.map((x, j) => (j === i ? next : x)))}
          onRemove={() => onChange(checks.filter((_, j) => j !== i))}
        />
      ))}
      <GhostButton className="self-start" onClick={() => onChange([...checks, makeCheck("contains", { text: "" })])}>
        <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Add check
      </GhostButton>
    </div>
  );
}

const parseTags = (s: string): string[] => [...new Set(String(s).split(",").map((t) => t.trim().toLowerCase()).filter(Boolean))];

/* `isNew` is explicit rather than inferred from `initial`, because a case
   captured from a trace arrives fully prefilled and is still a new case. */
export default function CaseDialog({ open, onOpenChange, initial, isNew = false, knownTags = [], onSave }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A case being edited, or one captured from a trace and not yet saved. */
  initial: EvalCase | null;
  isNew?: boolean;
  knownTags?: string[];
  onSave: (kase: EvalCase) => void;
}) {
  const start = initial || makeCase();
  const [name, setName] = useState(start.name);
  const [input, setInput] = useState(start.input);
  const [expected, setExpected] = useState(start.expected);
  const [tags, setTags] = useState((start.tags || []).join(", "));
  const [checks, setChecks] = useState(start.checks || []);

  const editing = !!initial && !isNew;
  const valid = name.trim() && input.trim();

  const save = () => {
    onSave({ ...start, name: name.trim(), input: input.trim(), expected, tags: parseTags(tags), checks });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 block w-screen h-[100dvh] rounded-none md:w-[min(620px,94vw)] md:h-auto md:rounded-[16px] sm:rounded-[16px] max-w-none overflow-hidden bg-[var(--canvas)] border border-[var(--hairline-strong)] shadow-dialog">
        <div className="px-5 pt-5 pb-4 border-b hairline">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] m-0">
            {editing ? "Edit case" : "New case"}
          </DialogTitle>
          <DialogDescription className="text-[13px] leading-[1.6] text-[var(--text-tertiary)] mt-1 mb-0">
            An input, what a good answer looks like, and the checks that decide whether the
            agent got there.
          </DialogDescription>
        </div>

        <div className="px-5 py-4 max-h-[62vh] md:max-h-[56vh] overflow-y-auto">
          {start.origin && (
            <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-[10px] bg-[var(--info-bg)] ring-1 ring-[var(--info-ring)]">
              <Waypoints className="w-3.5 h-3.5 shrink-0 text-[var(--info-text)]" strokeWidth={1.75} />
              <span className="text-[12px] text-[var(--info-text)]">
                Captured from a real run — the expected output below is what the agent
                actually said. Edit it to what it <em>should</em> have said.
              </span>
            </div>
          )}

          <FieldLabel>Name</FieldLabel>
          <TextInput value={name} onChange={setName} placeholder="e.g. Cites a source for every figure" />

          <div className="mt-4">
            <FieldLabel>Input</FieldLabel>
            <textarea
              rows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="What the agent is asked to do"
              className={textareaCls}
            />
          </div>

          <div className="mt-4">
            <FieldLabel>Expected output — optional</FieldLabel>
            <textarea
              rows={3}
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
              placeholder="What a good answer looks like. Leave empty if the checks below are enough."
              className={textareaCls}
            />
          </div>

          <div className="mt-4">
            <FieldLabel>Tags</FieldLabel>
            <TextInput value={tags} onChange={setTags} placeholder="comma separated — core, safety, format" />
            {knownTags.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                {knownTags.map((t) => (
                  <TagChip
                    key={t}
                    tag={t}
                    active={parseTags(tags).includes(t)}
                    onClick={() => {
                      const cur = parseTags(tags);
                      setTags((cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]).join(", "));
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="h-px bg-[var(--hairline)] my-5" />

          <div className="flex items-baseline justify-between mb-2.5">
            <FieldLabel>Checks for this case</FieldLabel>
            <span className="text-[11.5px] text-[var(--text-quaternary)]">
              Suite-wide checks run on top of these
            </span>
          </div>
          <CheckList checks={checks} onChange={setChecks} emptyHint="No case-specific checks — only the suite-wide ones will run." />
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t hairline">
          <GhostButton onClick={() => onOpenChange(false)}>Cancel</GhostButton>
          <PrimaryButton onClick={save} disabled={!valid}>
            {editing ? "Save case" : "Add case"}
          </PrimaryButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
